'use client';

import { useState } from 'react';
import { cn } from '../../lib/utils';

export function PasswordGenerator() {
  const [password, setPassword] = useState('');
  const [length, setLength] = useState(16);
  const [useUppercase, setUseUppercase] = useState(true);
  const [useLowercase, setUseLowercase] = useState(true);
  const [useNumbers, setUseNumbers] = useState(true);
  const [useSymbols, setUseSymbols] = useState(true);
  const [copied, setCopied] = useState(false);
  const [strength, setStrength] = useState('');

  const generatePassword = () => {
    let charset = '';
    let newPassword = '';

    if (useLowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
    if (useUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (useNumbers) charset += '0123456789';
    if (useSymbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';

    if (charset === '') {
      setPassword('');
      setStrength('');
      return;
    }

    // Generate cryptographically secure random password
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);

    for (let i = 0; i < length; i++) {
      newPassword += charset[array[i] % charset.length];
    }

    setPassword(newPassword);
    calculateStrength(newPassword);
  };

  const calculateStrength = (pwd) => {
    let score = 0;

    // Length score
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (pwd.length >= 16) score += 1;

    // Complexity score
    if (/[a-z]/.test(pwd)) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^a-zA-Z0-9]/.test(pwd)) score += 1;

    if (score <= 2) setStrength('Schwach');
    else if (score <= 4) setStrength('Mittel');
    else if (score <= 6) setStrength('Stark');
    else setStrength('Sehr Stark');
  };

  const copyToClipboard = async () => {
    if (!password) return;

    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getStrengthColor = () => {
    switch (strength) {
      case 'Schwach': return 'text-red-600';
      case 'Mittel': return 'text-orange-600';
      case 'Stark': return 'text-yellow-600';
      case 'Sehr Stark': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h3 className="font-heading font-bold text-2xl mb-4">
        Passwort Generator
      </h3>
      <p className="text-gray-700 mb-6">
        Generiere sichere, zufällige Passwörter mit konfigurierbaren Regeln. Nutzt crypto.getRandomValues() für maximale Sicherheit.
      </p>

      {/* Length Slider */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold">
            Passwortlänge
          </label>
          <span className="text-accent font-bold text-lg">{length}</span>
        </div>
        <input
          type="range"
          min="8"
          max="64"
          value={length}
          onChange={(e) => setLength(parseInt(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-accent"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>8</span>
          <span>64</span>
        </div>
      </div>

      {/* Options Checkboxes */}
      <div className="space-y-3 mb-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={useUppercase}
            onChange={(e) => setUseUppercase(e.target.checked)}
            className="w-5 h-5 text-accent rounded focus:ring-accent cursor-pointer"
          />
          <span className="text-gray-700">Großbuchstaben (A-Z)</span>
        </label>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={useLowercase}
            onChange={(e) => setUseLowercase(e.target.checked)}
            className="w-5 h-5 text-accent rounded focus:ring-accent cursor-pointer"
          />
          <span className="text-gray-700">Kleinbuchstaben (a-z)</span>
        </label>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={useNumbers}
            onChange={(e) => setUseNumbers(e.target.checked)}
            className="w-5 h-5 text-accent rounded focus:ring-accent cursor-pointer"
          />
          <span className="text-gray-700">Zahlen (0-9)</span>
        </label>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={useSymbols}
            onChange={(e) => setUseSymbols(e.target.checked)}
            className="w-5 h-5 text-accent rounded focus:ring-accent cursor-pointer"
          />
          <span className="text-gray-700">Sonderzeichen (!@#$%...)</span>
        </label>
      </div>

      {/* Generate Button */}
      <button
        onClick={generatePassword}
        className="w-full bg-accent hover:bg-accent-dark text-white font-semibold px-6 py-4 rounded-lg transition-colors mb-6"
      >
        Passwort generieren
      </button>

      {/* Password Output */}
      {password && (
        <div className="space-y-4">
          <div className="bg-gray-900 text-green-400 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-400">Generiertes Passwort</span>
              <span className={cn('text-sm font-bold', getStrengthColor())}>
                {strength}
              </span>
            </div>
            <div className="font-mono text-lg break-all mb-3">
              {password}
            </div>
            <button
              onClick={copyToClipboard}
              className={cn(
                'w-full px-4 py-2 rounded-lg text-sm font-semibold transition-colors',
                copied
                  ? 'bg-green-500 text-white'
                  : 'bg-accent hover:bg-accent-dark text-white'
              )}
            >
              {copied ? '✓ Kopiert!' : 'Passwort kopieren'}
            </button>
          </div>

          {/* Security Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>⚠️ Wichtig:</strong> Speichere dieses Passwort sicher in einem Passwort-Manager.
              Diese Seite speichert keine Passwörter!
            </p>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>ℹ️ Hinweis:</strong> Passwörter werden lokal mit crypto.getRandomValues() generiert.
              Maximale Sicherheit, keine Server-Kommunikation.
            </p>
          </div>
        </div>
      )}

      {!password && (
        <div className="text-center text-gray-500 py-8">
          Klicke auf "Passwort generieren" um zu starten
        </div>
      )}

      {/* Feature List */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h4 className="font-semibold text-lg mb-4">Features</h4>
        <ul className="space-y-2">
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>Kryptographisch sicher:</strong> Nutzt crypto.getRandomValues() für echte Zufälligkeit
            </span>
          </li>
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>Konfigurierbar:</strong> Länge 8-64 Zeichen, wählbare Zeichentypen
            </span>
          </li>
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>Stärke-Anzeige:</strong> Automatische Bewertung der Passwortstärke
            </span>
          </li>
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>100% Client-Side:</strong> Keine Passwörter werden gespeichert oder übertragen
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
